import { formatOptionFieldValue, getCountryName, isOptionFieldType } from "@repo/shared";
import type { ClientListSortDir } from "@repo/shared/types";
import { Building2, Pencil, Trash2, User, Users } from "lucide-react";
import { useState, useTransition } from "react";
import { ClientAccessLock } from "@/components/clients/client-access-lock";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  SortableTableHead,
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
  TableRowLink,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { useClientIdentity, VerifiedIdBadge, VerifiedIdDialog } from "@/components/verified-id";
import { useConfirm } from "@/hooks/use-confirm";
import { del } from "@/lib/api/_client";
import { formatDateTimeFieldValue } from "@/lib/date-utils";
import { useClientAccessSettings } from "@/lib/hooks/use-app-bootstrap";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useInvalidateClientAccessData } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ClientWithCustomValues } from "@/lib/types/client";
import { displayUserName } from "@/lib/user-display";
import type { CustomField } from "../settings/settings-types";
import { T } from "./clients-table.i18n";

type ClientsTableProps = {
  /** The current page of clients, already filtered and ordered by the server. */
  clients: ClientWithCustomValues[];
  customFields: CustomField[];
  onEdit: (client: ClientWithCustomValues) => void;
  /** QL-102 — what to say when the list is empty; the default assumes no clients exist at all. */
  emptyMessage?: string;
  /**
   * QL-115 / QL-136 — the active sort column (a field id) and direction. The
   * server orders the whole list; the table only shows which header is active
   * and reports clicks.
   */
  sortBy: string;
  sortDir: ClientListSortDir;
  onSort: (fieldId: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** True while the first page loads; the empty state waits for it. */
  isLoading?: boolean;
  /** True while another page or filter is loading; the stale rows dim. */
  isFetching?: boolean;
};

export function ClientsTable({
  clients,
  customFields,
  onEdit,
  emptyMessage,
  sortBy,
  sortDir,
  onSort,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  isLoading,
  isFetching,
}: ClientsTableProps) {
  const [isPending, startTransition] = useTransition();
  // QL-e — one dialog for the whole table; a table of hundreds must not mount
  // hundreds of modals.
  const [identityFor, setIdentityFor] = useState<string | null>(null);
  const { identity: identityDetail } = useClientIdentity(identityFor);
  const { confirm, ConfirmationDialog } = useConfirm();
  const invalidateClientAccessData = useInvalidateClientAccessData();
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const { formatDateTime } = useRegionalFormat();
  const accessSettings = useClientAccessSettings();
  const { clientCostCategories } = useClientCostCategories();

  const costCategoryName = (id: string | null | undefined) =>
    id ? localize(clientCostCategories.find((c) => c.id === id)?.nameI18n) || "" || null : null;
  const t = useT(T);

  const hasNameColumn = customFields.some((field) => field.id === "standard-name");

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deleteClient,
      description: t.deleteConfirmation(name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/clients/${id}`);
        invalidateClientAccessData();
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err?.message || t.failedToDelete,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    });
  };

  const getFieldValue = (client: ClientWithCustomValues, field: CustomField) => {
    // Check if this is a standard field
    if (field.isStandard) {
      const fieldMap: Record<string, any> = {
        "standard-name": client.name,
        "standard-email": client.email,
        "standard-mobilePhone": client.mobilePhone,
        "standard-ssn": client.ssn,
        "standard-dateOfBirth": client.dateOfBirth,
        "standard-orgId": client.orgId,
        "standard-country": client.country ? getCountryName(client.country, userLanguage) : null,
        "standard-owner": displayUserName(client.owner, userLanguage),
        "standard-externalId": client.externalId,
        // QL-126 — the client stores the id; the column shows the name.
        "standard-costCategory": costCategoryName(client.clientCostCategoryId),
        // QL-380 — teams are labels too; shown whatever the access settings.
        "standard-teams": client.teams.map((team) => team.name).join(", "),
      };
      return fieldMap[field.id] || "-";
    }

    // Custom field - get from customValues
    const value = client.customValues.find((cv) => cv.customFieldId === field.id);
    if (isOptionFieldType(field.type) && value?.value) {
      // Stored values are option ids (QL-123) — resolve to localized labels.
      return formatOptionFieldValue(field.type, value.value, field.options, userLanguage) || "-";
    }
    if (field.type === "boolean" && value?.value) {
      return value.value === "true" ? t.yes : t.no;
    }
    if (field.type === "datetime" && value?.value) {
      return formatDateTimeFieldValue(value.value, formatDateTime);
    }
    return value?.value || "-";
  };

  if (isLoading) {
    return null;
  }

  if (clients.length === 0) {
    return <EmptyState>{emptyMessage ?? t.noClientsYet}</EmptyState>;
  }

  return (
    <>
      <Table
        variant="navigation"
        containerClassName={isFetching ? "opacity-50 pointer-events-none transition-opacity" : ""}
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            {customFields.map((field) => (
              <SortableTableHead
                key={field.id}
                direction={sortBy === field.id ? sortDir : null}
                onSort={() => onSort(field.id)}
              >
                {localize(field.nameI18n) || field.name}
              </SortableTableHead>
            ))}
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client) => {
            const href = `/clients/${client.id}`;
            const typeIcon =
              client.type === "Company" ? (
                <Building2 className="h-4 w-4 text-muted-foreground" />
              ) : client.type === "Group" ? (
                <Users className="h-4 w-4 text-muted-foreground" />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              );
            return (
              <TableRow key={client.id}>
                {/* One row link, read out once: on the name when that column is shown,
                  otherwise on the type icon labelled with the name (the columns are
                  tenant-configurable). */}
                {hasNameColumn ? (
                  <TableCell className="w-10">{typeIcon}</TableCell>
                ) : (
                  <TableCellLink className="w-10" to={href} label={client.name}>
                    {typeIcon}
                  </TableCellLink>
                )}
                {customFields.map((field) => (
                  <TableCell
                    key={field.id}
                    className={field.id === "standard-name" ? "font-medium" : ""}
                  >
                    {/* The badge/lock buttons sit above the row link so they stay clickable. */}
                    <span className="inline-flex items-center gap-1.5 [&>button]:relative [&>button]:z-10">
                      {field.id === "standard-name" ? (
                        <TableRowLink to={href}>{getFieldValue(client, field)}</TableRowLink>
                      ) : (
                        getFieldValue(client, field)
                      )}
                      {/* QL-e — after the NAME only. The other columns are
                        attributes, not the person, so a badge there would say
                        nothing. */}
                      {field.id === "standard-name" && (
                        <VerifiedIdBadge
                          summary={client.identity}
                          onOpen={() => setIdentityFor(client.id)}
                        />
                      )}
                      {field.id === "standard-name" && (
                        <ClientAccessLock
                          settings={accessSettings}
                          isPrivate={client.isPrivate}
                          restriction={client.accessRestriction}
                          ownerName={client.owner?.name}
                          teamNames={client.teams.map((team) => team.name)}
                        />
                      )}
                    </span>
                  </TableCell>
                ))}
                <TableActionsCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.edit}
                    onClick={() => onEdit(client)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.delete}
                    onClick={() => handleDelete(client.id, client.name)}
                    disabled={isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableActionsCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
      <VerifiedIdDialog
        open={identityFor !== null}
        onClose={() => setIdentityFor(null)}
        verifications={identityDetail?.verifications ?? []}
      />
      <ConfirmationDialog />
    </>
  );
}
